import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import API, { authFetch } from './api';
import { TriStateValue, ListChips, DetailRow, petsSummary } from './ListingFieldControls';

// A section is worth rendering if ANY of its fields was stated (false counts as stated).
const hasAny = (vals) => vals.some((v) => v !== null && v !== undefined && v !== '');

export default function PropertyDetail() {
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'buyers' ? 'buyers' : 'details');
  const [buyerMatches, setBuyerMatches] = useState([]);
  const [loadingBuyers, setLoadingBuyers] = useState(false);

  useEffect(() => { loadProperty(); }, [propertyId]);
  useEffect(() => { if (activeTab === 'buyers') loadBuyerMatches(); }, [activeTab, propertyId]);

  const loadProperty = async () => {
    try {
      const res = await authFetch(`${API}/api/properties/${propertyId}`);
      if (res.ok) setProperty(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadBuyerMatches = async () => {
    setLoadingBuyers(true);
    try {
      const buyersRes = await authFetch(`${API}/api/buyers`);
      if (!buyersRes.ok) { setLoadingBuyers(false); return; }
      const buyers = await buyersRes.json();

      const matches = [];
      for (const buyer of buyers) {
        const scoreRes = await authFetch(`${API}/api/grades/fit-score/${propertyId}/buyer/${buyer.id}`);
        let scoreData = null;
        if (scoreRes.ok) scoreData = await scoreRes.json();

        let budget = null;
        try {
          const measRes = await authFetch(`${API}/api/measurables/definitions/${buyer.id}`);
          if (measRes.ok) {
            const defs = await measRes.json();
            const budgetDef = defs.find(d => d.name.toLowerCase().includes('budget') || d.name.toLowerCase().includes('max price'));
            if (budgetDef?.target_value) budget = parseFloat(budgetDef.target_value);
          }
        } catch (e) { /* silent */ }

        if (scoreData && scoreData.overall) {
          matches.push({ ...buyer, fit_score: scoreData.overall, tier: scoreData.tier, budget });
        }
      }

      matches.sort((a, b) => b.fit_score - a.fit_score);
      setBuyerMatches(matches);
    } catch (err) { console.error(err); }
    finally { setLoadingBuyers(false); }
  };

  const formatPrice = (p) => p ? `$${Number(p).toLocaleString()}` : 'N/A';

  const getPriceColor = (budget) => {
    if (!budget || !property?.price) return 'text-[#00E676]';
    const ratio = parseFloat(property.price) / budget;
    if (ratio <= 1.0) return 'text-[#00E676]';
    if (ratio <= 1.025) return 'text-yellow-300';
    if (ratio <= 1.05) return 'text-yellow-400';
    if (ratio <= 1.075) return 'text-orange-400';
    if (ratio <= 1.10) return 'text-orange-500';
    return 'text-red-400';
  };

  const getTimelineLabel = (t) => ({ '30_days': '30 days', '60_days': '60 days', '90_days': '90 days', '6_months': '6 months', 'exploring': 'Exploring' }[t] || 'Not set');
  const getTimelineColor = (t) => ({ '30_days': 'text-red-400 font-semibold', '60_days': 'text-orange-400', '90_days': 'text-yellow-400', '6_months': 'text-blue-400' }[t] || 'text-gray-500');
  const getPreApprovalLabel = (s) => ({ 'approved': 'Approved', 'in_process': 'In Process', 'not_started': 'Not Started', 'unknown': 'Unknown' }[s] || 'Unknown');
  const getPreApprovalColor = (s) => ({ 'approved': 'text-[#00E676]', 'in_process': 'text-yellow-400', 'not_started': 'text-orange-400' }[s] || 'text-gray-500');
  const getMotivationLabel = (m) => ({ 'must_move': 'Must Move', 'want_to_move': 'Want to Move', 'casually_looking': 'Casual' }[m] || 'Not set');
  const getMotivationColor = (m) => ({ 'must_move': 'text-red-400 font-semibold', 'want_to_move': 'text-yellow-400' }[m] || 'text-gray-500');

  const getTierBadge = (tier) => {
    const colors = {
      'ELITE FIT': 'bg-emerald-900/70 text-emerald-300 border-emerald-500/30',
      'STRONG FIT': 'bg-green-900/50 text-green-300 border-green-500/20',
      'GOOD FIT': 'bg-yellow-900/40 text-yellow-300 border-yellow-500/20',
      'MARGINAL FIT': 'bg-orange-900/40 text-orange-300 border-orange-500/20',
      'POOR FIT': 'bg-red-900/40 text-red-300 border-red-500/20',
    };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium border ${colors[tier] || 'bg-gray-800 text-gray-400'}`}>{tier}</span>;
  };

  if (loading) return <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center"><div className="text-[#00D4FF]">Loading...</div></div>;
  if (!property) return <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center"><div className="text-red-400">Property not found</div></div>;

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <Link to="/agent-board" className="text-[#00D4FF] text-sm hover:text-[#00E676] mb-2 inline-block">← Back to Master Board</Link>
        <h1 className="text-xl md:text-2xl font-bold text-white break-words">{property.address}</h1>
        <p className="text-gray-400">{property.city}, {property.state} {property.zip}</p>
        {property.mls_number && <p className="text-gray-500 text-sm">MLS: {property.mls_number}</p>}

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mt-6 mb-6 border-b border-[#546E7A]/30">
          <button onClick={() => setActiveTab('details')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'details' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            Property Details
          </button>
          <button onClick={() => setActiveTab('buyers')}
            className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'buyers' ? 'text-[#00D4FF] border-[#00D4FF]' : 'text-gray-400 border-transparent hover:text-white'}`}>
            See Buyers {buyerMatches.length > 0 && `(${buyerMatches.length})`}
          </button>
        </div>

        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Property Details</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Price:</span><br/><span className="text-[#00E676] font-bold text-lg">{formatPrice(property.price)}</span></div>
                  <div><span className="text-gray-500">Price/Sqft:</span><br/><span className="text-white">{property.sqft && property.price ? `$${Math.round(property.price / property.sqft)}` : 'N/A'}</span></div>
                  <div><span className="text-gray-500">Bedrooms:</span><br/><span className="text-white">{property.beds || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Bathrooms:</span><br/><span className="text-white">{property.baths || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Square Feet:</span><br/><span className="text-white">{property.sqft ? property.sqft.toLocaleString() : 'N/A'}</span></div>
                  <div><span className="text-gray-500">Lot Size:</span><br/><span className="text-white">{property.lot_size_acres ? `${property.lot_size_acres} acres` : 'N/A'}</span></div>
                  <div><span className="text-gray-500">Year Built:</span><br/><span className="text-white">{property.year_built || 'N/A'}</span></div>
                  <div><span className="text-gray-500">Garage:</span><br/><span className="text-white">{property.garage_spaces ? `${property.garage_spaces} spaces` : 'N/A'}</span></div>
                  <div><span className="text-gray-500">HOA:</span><br/><span className="text-white">{property.hoa_monthly ? `$${property.hoa_monthly}/month` : 'None'}</span></div>
                  <div><span className="text-gray-500">Type:</span><br/><span className="text-white">{property.property_type?.replace('_', ' ') || 'N/A'}</span></div>
                </div>
              </div>
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Location & School</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="min-w-0"><span className="text-gray-500">School District:</span><br/><span className="text-white break-words">{property.school_district || 'N/A'}</span></div>
                  <div className="min-w-0"><span className="text-gray-500">School Rating:</span><br/><span className="text-white">{property.school_rating ? `${property.school_rating}/10` : 'N/A'}</span></div>
                  {property.elementary_school && <div className="min-w-0"><span className="text-gray-500">Elementary:</span><br/><span className="text-white break-words">{property.elementary_school}</span></div>}
                  {property.middle_school && <div className="min-w-0"><span className="text-gray-500">Middle:</span><br/><span className="text-white break-words">{property.middle_school}</span></div>}
                  {property.high_school && <div className="min-w-0"><span className="text-gray-500">High:</span><br/><span className="text-white break-words">{property.high_school}</span></div>}
                  {property.subdivision && <div className="min-w-0"><span className="text-gray-500">Subdivision:</span><br/><span className="text-white break-words">{property.subdivision}</span></div>}
                  {property.county && <div className="min-w-0"><span className="text-gray-500">County:</span><br/><span className="text-white break-words">{property.county}</span></div>}
                  <div><span className="text-gray-500">Status:</span><br/><span className={`font-medium ${property.listing_status === 'active' ? 'text-green-400' : property.listing_status === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>{property.listing_status}</span></div>
                  <div><span className="text-gray-500">Source:</span><br/><span className="text-white">{property.data_source === 'idx' ? 'MLS Import' : 'Manual'}</span></div>
                </div>
                {property.listing_url && (
                  <a href={property.listing_url} target="_blank" rel="noopener noreferrer"
                    className="inline-block mt-4 px-4 py-2 bg-[#243B55] text-[#00D4FF] rounded-lg text-sm font-medium hover:bg-[#2d4a6a]">
                    View Full MLS Listing →
                  </a>
                )}
              </div>
            </div>

            {/* Property Features (Pool, Stories, Furnished, Gated, Senior) */}
            {/* Property Features — a stated "No" and an unstated field are DIFFERENT things,
                so booleans render Yes / No / Not stated rather than appearing only when true. */}
            {hasAny([property.pool, property.stories, property.furnished, property.gated_community,
                     property.senior_community, property.accessibility_features,
                     property.bathrooms_full, property.bathrooms_half, property.pool_features]) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Property Features</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <DetailRow label="Pool" value={property.pool}><TriStateValue value={property.pool} /></DetailRow>
                  <DetailRow label="Stories" value={property.stories} />
                  <DetailRow label="Full baths" value={property.bathrooms_full} />
                  <DetailRow label="Half baths" value={property.bathrooms_half} />
                  <DetailRow label="Gated" value={property.gated_community}><TriStateValue value={property.gated_community} /></DetailRow>
                  <DetailRow label="Senior (55+)" value={property.senior_community}><TriStateValue value={property.senior_community} /></DetailRow>
                  <DetailRow label="Accessibility" value={property.accessibility_features}><TriStateValue value={property.accessibility_features} /></DetailRow>
                  <DetailRow label="Furnished" value={property.furnished} />
                </div>
                {property.pool_features && (
                  <div className="mt-3">
                    <span className="text-gray-500 text-xs">Pool features</span>
                    <div className="mt-1"><ListChips value={property.pool_features} /></div>
                  </div>
                )}
              </div>
            )}

            {/* Waterfront */}
            {hasAny([property.waterfront, property.waterfront_features, property.waterfront_type]) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Waterfront</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <DetailRow label="Waterfront" value={property.waterfront}><TriStateValue value={property.waterfront} /></DetailRow>
                </div>
                {(property.waterfront_features || property.waterfront_type) && (
                  <div className="mt-3">
                    <span className="text-gray-500 text-xs">Waterfront features</span>
                    <div className="mt-1"><ListChips value={property.waterfront_features || property.waterfront_type} /></div>
                  </div>
                )}
              </div>
            )}

            {/* Lifestyle */}
            {hasAny([property.pets_allowed, property.dogs_allowed, property.cats_allowed,
                     property.community_amenities, property.direction_faces]) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Lifestyle & Community</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mb-3">
                  <DetailRow label="Pets" value={petsSummary(property)} />
                  <DetailRow label="Dogs" value={property.dogs_allowed}><TriStateValue value={property.dogs_allowed} /></DetailRow>
                  <DetailRow label="Cats" value={property.cats_allowed}><TriStateValue value={property.cats_allowed} /></DetailRow>
                  <DetailRow label="Faces" value={property.direction_faces} />
                </div>
                {property.community_amenities && (
                  <div>
                    <span className="text-gray-500 text-xs">Community amenities</span>
                    <div className="mt-1"><ListChips value={property.community_amenities} tone="green" /></div>
                  </div>
                )}
              </div>
            )}

            {/* Utilities */}
            {hasAny([property.cooling, property.heating, property.appliances]) && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Utilities & Appliances</h3>
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <DetailRow label="Cooling" value={property.cooling} />
                  <DetailRow label="Heating" value={property.heating} />
                </div>
                {property.appliances && (
                  <div>
                    <span className="text-gray-500 text-xs">Appliances included</span>
                    <div className="mt-1"><ListChips value={property.appliances} /></div>
                  </div>
                )}
              </div>
            )}

            {/* Pricing History */}
            {property.original_list_price && property.original_list_price !== property.price && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-4">Pricing History</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Original List Price:</span><br/><span className="text-white">{formatPrice(property.original_list_price)}</span></div>
                  <div><span className="text-gray-500">Current Price:</span><br/><span className="text-[#00E676] font-medium">{formatPrice(property.price)}</span></div>
                </div>
              </div>
            )}

            {/* Description */}
            {property.description && (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-6">
                <h3 className="text-white font-semibold mb-3">Description</h3>
                <p className="text-gray-200 text-sm whitespace-pre-wrap leading-relaxed">{property.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Buyers Tab */}
        {activeTab === 'buyers' && (
          <div>
            {loadingBuyers ? (
              <div className="text-center py-12"><div className="text-[#00D4FF]">Loading buyer matches...</div></div>
            ) : buyerMatches.length === 0 ? (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-12">
                <p className="text-gray-400">No buyers have been scored for this property yet.</p>
              </div>
            ) : (
              <>
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 mb-4">
                  <p className="text-gray-400 text-sm">
                    {buyerMatches.length} buyer{buyerMatches.length !== 1 ? 's' : ''} matched to
                    <span className="text-white font-medium"> {property.address}</span> at
                    <span className="text-[#00E676] font-medium"> {formatPrice(property.price)}</span>
                    <span className="text-gray-500"> — ranked by fit score</span>
                  </p>
                </div>
                <div className="hidden md:block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#546E7A]/30">
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#B0BEC5]">Buyer</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Fit Score</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Tier</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Price vs Budget</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Timeline</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Pre-Approval</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Motivation</th>
                        <th className="text-center px-4 py-3 text-sm font-medium text-[#B0BEC5]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buyerMatches.map((buyer, i) => (
                        <tr key={buyer.id} className={`border-b border-[#546E7A]/10 hover:bg-[#243B55]/30 transition-colors ${i % 2 === 1 ? 'bg-[#0A0E27]/20' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="text-white font-medium text-sm">{buyer.name}</div>
                            <div className="text-gray-500 text-xs">{buyer.email}</div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-white font-bold">{Math.round((buyer.fit_score / 9) * 100)}%</span>
                          </td>
                          <td className="px-4 py-3 text-center">{getTierBadge(buyer.tier)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`font-bold ${getPriceColor(buyer.budget)}`}>{formatPrice(property.price)}</span>
                            {buyer.budget && <div className="text-gray-500 text-xs">Budget: {formatPrice(buyer.budget)}</div>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm ${getTimelineColor(buyer.timeline)}`}>{getTimelineLabel(buyer.timeline)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm ${getPreApprovalColor(buyer.pre_approval_status)}`}>{getPreApprovalLabel(buyer.pre_approval_status)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm ${getMotivationColor(buyer.motivation)}`}>{getMotivationLabel(buyer.motivation)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Link to={`/properties/${propertyId}/buyer/${buyer.id}`}
                              className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-xs font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors whitespace-nowrap">
                              Full Eval
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {buyerMatches.map((buyer) => (
                    <div key={buyer.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
                      <div className="flex justify-between items-start gap-3 mb-3">
                        <div className="min-w-0">
                          <div className="text-white font-medium text-sm truncate">{buyer.name}</div>
                          <div className="text-gray-500 text-xs truncate">{buyer.email}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-white font-bold">{Math.round((buyer.fit_score / 9) * 100)}%</div>
                          <div className="mt-1">{getTierBadge(buyer.tier)}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div>
                          <div className="text-gray-500">Price vs Budget</div>
                          <div className={`font-bold ${getPriceColor(buyer.budget)}`}>{formatPrice(property.price)}</div>
                          {buyer.budget && <div className="text-gray-500 text-[11px]">Budget: {formatPrice(buyer.budget)}</div>}
                        </div>
                        <div>
                          <div className="text-gray-500">Timeline</div>
                          <div className={getTimelineColor(buyer.timeline)}>{getTimelineLabel(buyer.timeline)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Pre-Approval</div>
                          <div className={getPreApprovalColor(buyer.pre_approval_status)}>{getPreApprovalLabel(buyer.pre_approval_status)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Motivation</div>
                          <div className={getMotivationColor(buyer.motivation)}>{getMotivationLabel(buyer.motivation)}</div>
                        </div>
                      </div>
                      <Link to={`/properties/${propertyId}/buyer/${buyer.id}`}
                        className="block text-center px-3 py-2 bg-[#243B55] text-[#00D4FF] text-xs font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors">
                        Full Eval
                      </Link>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
